import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';

import type { Session } from '../modules/auth/domain/entities/session';
import { readOfflineProfile } from '../modules/offline/application/offline-profile-store';
import { offlineDb } from '../modules/offline/infrastructure/dexie/offline-db';
import { DexieKvStore } from '../modules/offline/infrastructure/dexie-kv';
import {
  createDexieAsyncStorage,
  shouldPersistQuery,
} from '../modules/offline/infrastructure/query-persister';
import { env } from './env';

export const queryClient = new QueryClient();

const RQ_CACHE_KV_KEY = 'rq-cache';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // design §5 — 24h

let persistenceStarted = false;

/**
 * Resolves which org's buster to use at boot: the in-memory session's org
 * when bootstrapSession() recovered one (normal online boot), otherwise the
 * cached offline profile's org (fully-offline reopen, task 3.6) — so a
 * device that never regains connectivity still restores ITS org's own
 * persisted cache on the next offline boot instead of always falling back
 * to an org-less 'anon' buster.
 */
export async function resolveBootOrgId(
  session: Session | null,
  kv: DexieKvStore,
): Promise<string | null> {
  if (session?.user.organizationId) return session.user.organizationId;
  const profile = await readOfflineProfile(kv).catch(() => null);
  return profile?.orgId ?? null;
}

/**
 * Wires TanStack Query's IndexedDB persister (design §5 / task 3.5).
 *
 * Deliberately NOT called at module scope: it must run AFTER
 * `bootstrapSession()` (main.tsx) settles, so `buster` reflects whichever
 * org is actually active at this boot — computing it at import time would
 * always see an empty auth store (bootstrapSession hasn't resolved yet) and
 * bake an org-less buster into every persisted cache regardless of who is
 * really logged in.
 *
 * Idempotent — safe to call more than once (only the first call takes
 * effect) since it is a one-time boot-time restore + write-through wiring,
 * not something that needs to be re-run per navigation.
 */
export function initQueryPersistence(orgId: string | null): void {
  if (persistenceStarted) return;
  persistenceStarted = true;

  const kv = new DexieKvStore(offlineDb);
  const persister = createAsyncStoragePersister({
    storage: createDexieAsyncStorage(kv),
    key: RQ_CACHE_KV_KEY,
  });

  void persistQueryClient({
    queryClient,
    persister,
    maxAge: MAX_AGE_MS,
    // A deploy with an incompatible cache shape, or a different org/no org
    // at all, must never restore another buster's data — this is the ONLY
    // enforcement point for "cleared on user/org change" at BOOT time (see
    // also queryClient.clear() at sign-out for the same-tab relogin case,
    // wired in _authenticated.tsx).
    buster: `${env.appVersion ?? 'dev'}:${orgId ?? 'anon'}`,
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => shouldPersistQuery(query.queryKey),
    },
  });
}

/**
 * Boot-time convenience wrapper — main.tsx calls this once, right after
 * `bootstrapSession()` settles, passing whatever session it recovered
 * (or null). Combines resolveBootOrgId() + initQueryPersistence() so
 * main.tsx does not need to know about the Dexie kv store directly.
 */
export async function startQueryPersistenceForBoot(session: Session | null): Promise<void> {
  const kv = new DexieKvStore(offlineDb);
  const orgId = await resolveBootOrgId(session, kv);
  initQueryPersistence(orgId);
}

/**
 * Adversarial-review fix (Slice 3, finding #1): explicitly and
 * synchronously purges the persisted `rq-cache` bytes from the Dexie `kv`
 * table. `queryClient.clear()` alone is NOT enough — it only clears the
 * in-memory cache; the persisted bytes only get overwritten LATER,
 * asynchronously, via the persister's own cache-event subscription (a
 * batched, best-effort side effect, not something to rely on at the exact
 * moment of an explicit sign-out). Called from `_authenticated.tsx`'s
 * `handleSignOut()` alongside `queryClient.clear()`. Only ever touches the
 * `rq-cache` kv row — never `offlineProfile`, never `outbox`/`blobs`.
 */
export async function clearPersistedQueryCache(): Promise<void> {
  const kv = new DexieKvStore(offlineDb);
  await kv.remove(RQ_CACHE_KV_KEY);
}
